import { useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Form, InputGroup, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import useApi from "../../Hooks/useApi";
import * as api from "../../api/getapi";
import * as postapi from "../../api/postapi";
import { isValidUrl } from "../../Utilities/url";

const ListingReviewsDummyUpsertModal = ({ initialData, onSuccess, onCancel }) => {
  const isEdit = Boolean(initialData?.id);

  const [validated, setValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [listingFetchInput, setListingFetchInput] = useState("");
  const [listingFetchInputID, setListingFetchInputID] = useState("");
  const [listingFetchInputError, setListingFetchInputError] = useState("");
  const [fetchedListing, setFetchedListing] = useState(null);

  const [rating, setRating] = useState("");
  const [review, setReview] = useState("");
  const [approveImmediately, setApproveImmediately] = useState(true);

  // img0 / img1: existing S3 key (string) or null — shown as thumbnails in edit mode
  const [existingImg0, setExistingImg0] = useState(null);
  const [existingImg1, setExistingImg1] = useState(null);
  // new local file selections (File objects with preview URLs)
  const [newImg0, setNewImg0] = useState(null);
  const [newImg1, setNewImg1] = useState(null);
  const img0InputRef = useRef(null);
  const img1InputRef = useRef(null);

  const createDummyListingReview = useApi(postapi.createDummyListingReview, { hasCatchError: true });
  const updateDummyListingReview = useApi(postapi.updateDummyListingReview, { hasCatchError: true });
  const fetchOrderListing = useApi(api.fetchOrderListing, { hasCatchError: true });

  useEffect(() => {
    if (isEdit) {
      setRating(initialData?.rating != null ? String(initialData.rating) : "");
      setReview(initialData?.review != null ? String(initialData.review) : "");
      setApproveImmediately(initialData?.is_pending === 0);
      setExistingImg0(initialData?.img0 ?? null);
      setExistingImg1(initialData?.img1 ?? null);
    } else {
      setListingFetchInput("");
      setListingFetchInputID("");
      setListingFetchInputError("");
      setFetchedListing(null);
      setRating("");
      setReview("");
      setApproveImmediately(true);
      setExistingImg0(null);
      setExistingImg1(null);
    }
    setNewImg0(null);
    setNewImg1(null);
  }, [isEdit, initialData]);

  const handleImgFileChange = (slot, e) => {
    const file = e.target.files?.[0] ?? null;
    if (!file) return;
    const preview = URL.createObjectURL(file);
    if (slot === 0) {
      setNewImg0({ file, preview });
    } else {
      setNewImg1({ file, preview });
    }
    e.target.value = "";
  };

  const clearImg = (slot) => {
    if (slot === 0) {
      if (newImg0?.preview) URL.revokeObjectURL(newImg0.preview);
      setNewImg0(null);
      setExistingImg0(null);
    } else {
      if (newImg1?.preview) URL.revokeObjectURL(newImg1.preview);
      setNewImg1(null);
      setExistingImg1(null);
    }
  };

  const handleListingFetchInput = (e) => {
    setListingFetchInputError("");
    setListingFetchInput(e.target.value);

    if (!isNaN(e.target.value)) {
      setListingFetchInputID(e.target.value);
    } else {
      if (isValidUrl(e.target.value)) {
        const urlArray = e.target.value.split("-");
        const id = urlArray[urlArray.length - 1];
        if (!isNaN(id)) {
          setListingFetchInputID(id);
        } else {
          setListingFetchInputError("Please Enter a valid Link");
        }
      } else {
        setListingFetchInputError("Please Enter Listing ID or a valid Link");
      }
    }
  };

  const handleFetchListing = async () => {
    try {
      if (listingFetchInputID?.length > 0) {
        const res = await fetchOrderListing.request(listingFetchInputID);
        setFetchedListing(res?.data?.data);
      } else {
        setListingFetchInputError("Please Enter Listing ID or a valid Link");
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        setListingFetchInputError(error?.response?.data?.message);
      } else {
        setListingFetchInputError(error?.message);
      }
    }
  };

  const isPendingValue = useMemo(() => {
    return approveImmediately ? 0 : 1;
  }, [approveImmediately]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setValidated(true);

    const ratingNum = rating === "" ? null : Number(String(rating).trim());
    const isRatingValid = ratingNum !== null && Number.isFinite(ratingNum) && ratingNum >= 1 && ratingNum <= 5;
    const reviewValue = String(review || "").trim();
    const isReviewValid = reviewValue.length > 0;

    if (!isRatingValid) {
      toast.error("Rating is required and must be between 1 and 5", {
        position: "bottom-right",
        autoClose: 2000,
      });
      return;
    }

    if (!isReviewValid) {
      toast.error("Review is required", {
        position: "bottom-right",
        autoClose: 2000,
      });
      return;
    }

    if (!isEdit && !fetchedListing?.listing?.listing_id) {
      toast.error("Listing is required. Please fetch a valid listing first", {
        position: "bottom-right",
        autoClose: 2000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      // Upload any new image files to CDN first
      let resolvedImg0 = existingImg0 ?? null;
      let resolvedImg1 = existingImg1 ?? null;

      const filesToUpload = [newImg0?.file, newImg1?.file].filter(Boolean);
      if (filesToUpload.length > 0) {
        const formData = new FormData();
        filesToUpload.forEach((f) => formData.append("files", f, f.name));
        const uploadRes = await postapi.uploadReviewImages(formData);
        const uploadedKeys = uploadRes?.data?.files ?? [];
        let keyIdx = 0;
        if (newImg0?.file) resolvedImg0 = uploadedKeys[keyIdx++] ?? null;
        if (newImg1?.file) resolvedImg1 = uploadedKeys[keyIdx++] ?? null;
      }

      const payload = {
        fk_listing_id: isEdit ? initialData.fk_listing_id : fetchedListing.listing.listing_id,
        rating: ratingNum,
        review: reviewValue,
        is_pending: isPendingValue,
        img0: resolvedImg0,
        img1: resolvedImg1,
      };

      if (isEdit) {
        await updateDummyListingReview.request(initialData.id, payload);
        toast.success("Dummy review updated successfully", {
          position: "bottom-right",
          autoClose: 1500,
        });
      } else {
        await createDummyListingReview.request(payload);
        toast.success("Dummy review created successfully", {
          position: "bottom-right",
          autoClose: 1500,
        });
      }
      setValidated(false);
      onSuccess();
    } catch (err) {
      toast.error(err?.response?.data?.error?.message || "Request failed", {
        position: "bottom-right",
        autoClose: 1500,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Form noValidate validated={validated} onSubmit={onSubmit}>
      {!isEdit && (
        <>
          {fetchedListing ? (
            <div className="position-relative mb-3">
              <div className="d-flex gap-2">
                <img
                  style={{ objectFit: "contain" }}
                  alt={fetchedListing?.listing?.listing_title}
                  src={`${process.env.REACT_APP_URL_S3}/${fetchedListing?.listing?.img0}`}
                  width={80}
                  height={60}
                />
                <div className="d-flex flex-column">
                  <span>{fetchedListing?.listing?.listing_title}</span>
                  <span>Listing ID: {fetchedListing?.listing?.listing_id}</span>
                </div>
              </div>
              <div className="position-absolute top-0 end-0">
                <Button
                  size="sm"
                  variant="outline-secondary"
                  type="button"
                  onClick={() => {
                    setFetchedListing(null);
                    setListingFetchInput("");
                    setListingFetchInputID("");
                    setListingFetchInputError("");
                  }}
                >
                  Change
                </Button>
              </div>
            </div>
          ) : (
            <Form.Group className="mb-3" controlId="listingSearch">
              <Form.Label>
                Link/Listing ID<span className="text-danger">*</span>
              </Form.Label>
              <InputGroup>
                <Form.Control placeholder="Enter Listing ID or Link..." value={listingFetchInput} onChange={handleListingFetchInput} required />
                <Button variant="primary" type="button" onClick={handleFetchListing}>
                  Fetch
                </Button>
              </InputGroup>
              {listingFetchInputError?.length > 0 && <div className="text-danger text-center mb-1 rounded-1 w-100">*{listingFetchInputError}</div>}
              {!(listingFetchInputError?.length > 0) && validated && <div className="text-danger text-center mb-1 rounded-1 w-100">*Please Select Listing</div>}
            </Form.Group>
          )}
        </>
      )}

      {isEdit && (
        <Form.Group className="mb-3">
          <Form.Label>Listing ID</Form.Label>
          <Form.Control type="text" value={String(initialData?.fk_listing_id ?? "")} disabled />
        </Form.Group>
      )}

      <Row>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>Rating</Form.Label>
            <Form.Select value={rating} onChange={(e) => setRating(e.target.value)} required isInvalid={validated && !(Number(rating) >= 1 && Number(rating) <= 5)}>
              <option value="">Select</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
            </Form.Select>
            <Form.Control.Feedback type="invalid">Rating is required (1..5).</Form.Control.Feedback>
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>Approval</Form.Label>
            <Form.Check
              type="checkbox"
              label="Approve immediately"
              checked={approveImmediately}
              onChange={(e) => setApproveImmediately(e.target.checked)}
            />
            <div className="text-muted" style={{ fontSize: "12px" }}>
              Creates/updates with is_pending = {isPendingValue}
            </div>
          </Form.Group>
        </Col>
      </Row>

      <Form.Group className="mb-3">
        <Form.Label>Review</Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          value={review}
          onChange={(e) => setReview(e.target.value)}
          required
          isInvalid={validated && String(review || "").trim().length === 0}
          placeholder="Write the review..."
        />
        <Form.Control.Feedback type="invalid">Review is required.</Form.Control.Feedback>
      </Form.Group>

      <Row>
        {[0, 1].map((slot) => {
          const existingKey = slot === 0 ? existingImg0 : existingImg1;
          const newFile = slot === 0 ? newImg0 : newImg1;
          const inputRef = slot === 0 ? img0InputRef : img1InputRef;
          const previewSrc = newFile
            ? newFile.preview
            : existingKey
            ? `${process.env.REACT_APP_URL_S3}/${existingKey}`
            : null;

          return (
            <Col lg={6} key={slot}>
              <Form.Group className="mb-3">
                <Form.Label>Image {slot + 1} (optional)</Form.Label>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  ref={inputRef}
                  style={{ display: "none" }}
                  onChange={(e) => handleImgFileChange(slot, e)}
                />
                {previewSrc ? (
                  <div className="d-flex align-items-center gap-2">
                    <a href={previewSrc} target="_blank" rel="noopener noreferrer">
                      <img
                        src={previewSrc}
                        alt={`img${slot} preview`}
                        style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 4, border: "1px solid #dee2e6" }}
                      />
                    </a>
                    <div className="d-flex flex-column gap-1">
                      <Button size="sm" variant="outline-secondary" type="button" onClick={() => inputRef.current?.click()}>
                        Replace
                      </Button>
                      <Button size="sm" variant="outline-danger" type="button" onClick={() => clearImg(slot)}>
                        Remove
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button size="sm" variant="outline-secondary" type="button" onClick={() => inputRef.current?.click()}>
                    Choose image
                  </Button>
                )}
              </Form.Group>
            </Col>
          );
        })}
      </Row>

      <Row className="mt-2">
        <Col>
          <Button variant="outline-secondary" className="w-100" type="button" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
        </Col>
        <Col>
          <Button className="w-100" type="submit" disabled={isSubmitting}>
            {isEdit ? "Update" : "Create"}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default ListingReviewsDummyUpsertModal;
